import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getSession } from './authentication';
import { configuration } from './configuration';
import { NotAuthenticatedError } from './errors/not-authenticated-error';

const httpLink = createHttpLink({ uri: configuration.backend.graphqlUrl });

// Reads the session on every request so a client created before sign-in
// still sends the right token afterwards (the legacy per-call clients got
// this for free by being constructed per request).
const authLink = setContext((_operation, { headers }) => {
  const session = getSession();
  if (!session) {
    return { headers };
  }
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${session.token}`,
    },
  };
});

// The legacy apollo-boost setup created a fresh client + cache per service
// call, so nothing in the app ever read a cached result. network-only on
// client.query keeps that "always fresh" behavior for the imperative service
// layer; watched queries (useQuery) keep Apollo's cache-first default — every
// current hook sets its policy explicitly.
const defaultOptions = {
  query: { fetchPolicy: 'network-only' as const },
};

// `viewer` is an idless singleton written by several queries with disjoint
// selections (account vs featureFlags); without a merge policy each write
// replaces the previous one and Apollo drops the other query's subtree.
const buildCache = () =>
  new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          viewer: { merge: true },
        },
      },
    },
  });

const publicClient = new ApolloClient({
  link: httpLink,
  cache: buildCache(),
  defaultOptions,
});

export const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: buildCache(),
  defaultOptions,
});

export const getGraphQLClient = (): ApolloClient<object> => {
  return publicClient;
};

export const getAuthenticatedGraphQLClient = (): ApolloClient<object> => {
  if (!getSession()) {
    throw new NotAuthenticatedError();
  }
  return apolloClient;
};
